
"use strict";

{
    const uid = () => {
        return Date.now().toString(36) +
            Math.random().toString(36).substring(2);
    };

    class QuickAdd {
        constructor({ elem }) {
            this.uniq = uid();
            this.isAdding = false;
            this.isPointerInteracting = false;
            this.openedByKeyboard = false;
            this.closingTimer = null;

            this.dom = {
                main: elem,
                wrapper: elem.closest('.quick-add-wrapper') || elem,
                trigger: elem.querySelector('.quick-add__open'),
                content: elem.querySelector('.quick-add__content'),
                close: elem.querySelector('.quick-add__close'),
                radios: elem.querySelectorAll('.SizeSwatch__Radio'),
                labels: elem.querySelectorAll('.SizeSwatch__Label'),
                price: elem.querySelector('.quick-add__price [data-price]'),
                compareAtPrice: elem.querySelector('.quick-add__price [data-compare-at-price]'),
                quantitySelector: {
                    increase: elem.querySelector(
                        '.quick-add__quantitySelector [data-action="increase-quantity"]'
                    ),
                    decrease: elem.querySelector(
                        '.quick-add__quantitySelector [data-action="decrease-quantity"]'
                    ),
                    input: elem.querySelector(
                        '.quick-add__quantitySelector input'
                    )
                },
                submit: elem.querySelector('.quick-add__add-to-cart'),
                status: elem.querySelector('.quick-add__cart-status')
            };

            elem.classList.remove('js-quick-add');

            this.init();
        }

        init() {
            this.uniqify();
            this.createStatusRegion();
            this.initDisclosure();
            this.addEventListeners();
            this.changeQuantity();
            this.submitClick();
        }

        initDisclosure() {
            if (!this.dom.content || !this.dom.trigger) return;

            this.dom.content.id = this.dom.content.id ||
                `quick-add-content-${this.uniq}`;
            this.dom.content.inert = true;
            this.dom.content.setAttribute('aria-hidden', 'true');
            this.dom.trigger.setAttribute('aria-controls', this.dom.content.id);
            this.dom.trigger.setAttribute('aria-expanded', 'false');
        }

        open({ isKeyboard = false } = {}) {
            clearTimeout(this.closingTimer);
            this.openedByKeyboard = isKeyboard;
            this.dom.main.classList.add('open');
            this.dom.content.inert = false;
            this.dom.content.setAttribute('aria-hidden', 'false');
            this.dom.trigger.setAttribute('aria-expanded', 'true');
            this.dom.trigger.hidden = true;

            if (this.dom.close) {
                this.dom.close.focus();
            }
        }

        close({ restoreFocus = false } = {}) {
            clearTimeout(this.closingTimer);
            this.dom.main.classList.remove('open');

            if (this.dom.content) {
                this.dom.content.inert = true;
                this.dom.content.setAttribute('aria-hidden', 'true');
            }

            if (this.dom.trigger) {
                this.dom.trigger.hidden = false;
                this.dom.trigger.setAttribute('aria-expanded', 'false');

                if (restoreFocus) {
                    this.dom.trigger.focus();
                }
            }
        }

        // Give each size input a unique ID
        uniqify() {
            this.dom.radios.forEach((radio, index) => {
                const variantId = radio.getAttribute('data-variant-id');
                const uniqueId = variantId + '-' + this.uniq;

                radio.setAttribute('name', this.uniq);
                radio.setAttribute('id', uniqueId);

                if (this.dom.labels[index]) {
                    this.dom.labels[index].setAttribute('for', uniqueId);
                }
            });
        }

        // WCAG: Create a persistent live region
        createStatusRegion() {
            if (!this.dom.status) {
                const status = document.createElement('div');

                status.className =
                    'quick-add__cart-status u-visually-hidden';

                (this.dom.content || this.dom.submit).insertAdjacentElement(
                    'afterend',
                    status
                );

                this.dom.status = status;
            }

            if (this.dom.content && this.dom.content.contains(this.dom.status)) {
                this.dom.content.insertAdjacentElement(
                    'afterend',
                    this.dom.status
                );
            }

            this.dom.status.setAttribute('role', 'status');
            this.dom.status.setAttribute('aria-live', 'polite');
            this.dom.status.setAttribute('aria-atomic', 'true');

            // Ensure the region is empty initially
            this.dom.status.textContent = '';
        }

        announce(message) {
            if (!this.dom.status) return;

            this.dom.status.textContent = message;
        }

        translatedMessage(template, values = {}) {
            return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
                values[key] === undefined ? match : values[key]
            );
        }

        addEventListeners() {
            const cancelClose = () => {
                clearTimeout(this.closingTimer);
            };

            const closeIfInactive = () => {
                const focusRemainsInQuickAdd =
                    this.dom.main.contains(document.activeElement);
                const focusIsStillOnMouseOpenedCloseButton =
                    !this.openedByKeyboard &&
                    document.activeElement === this.dom.close;

                if (
                    (focusRemainsInQuickAdd && !focusIsStillOnMouseOpenedCloseButton) ||
                    this.dom.wrapper.matches(':hover') ||
                    this.dom.main.matches(':hover')
                ) {
                    return;
                }

                this.close();
            };

            if (this.dom.trigger) {
                this.dom.trigger.addEventListener('click', (event) => {
                    this.open({ isKeyboard: event.detail === 0 });
                });
            }

            if (this.dom.close) {
                this.dom.close.addEventListener('click', () => {
                    this.close({ restoreFocus: true });
                });
            }

            this.dom.wrapper.addEventListener('mouseleave', () => {
                this.closingTimer = setTimeout(
                    closeIfInactive,
                    1000
                );
            });

            this.dom.wrapper.addEventListener(
                'mouseenter',
                cancelClose
            );

            this.dom.main.addEventListener(
                'mouseenter',
                cancelClose
            );

            if (this.dom.content) {
                this.dom.content.addEventListener(
                    'mouseenter',
                    cancelClose
                );

                this.dom.content.addEventListener(
                    'click',
                    cancelClose
                );
            }

            this.dom.submit.addEventListener(
                'mouseenter',
                cancelClose
            );

            this.dom.radios.forEach(radio => {
                radio.addEventListener('change', () => {
                    cancelClose();
                    this.dom.main.classList.add('open');
                });
            });

            // WCAG: Close using Escape
            this.dom.main.addEventListener('keydown', (e) => {
                if (
                    e.key === 'Escape' &&
                    this.dom.main.classList.contains('open')
                ) {
                    e.preventDefault();
                    this.close({ restoreFocus: true });
                }
            });

            // Close when focus leaves
            this.dom.main.addEventListener('focusout', (e) => {
                if (
                    !this.isPointerInteracting &&
                    !this.dom.main.contains(e.relatedTarget)
                ) {
                    setTimeout(closeIfInactive, 0);
                }
            });

            this.dom.main.addEventListener('pointerdown', () => {
                this.isPointerInteracting = true;
            });

            this.dom.main.addEventListener('pointerup', () => {
                setTimeout(() => {
                    this.isPointerInteracting = false;
                }, 0);
            });
        }

        changeQuantity() {
            const min = 1;
            const max = 99;

            const selector = this.dom.quantitySelector;

            if (
                !selector.increase ||
                !selector.decrease ||
                !selector.input
            ) {
                return;
            }

            selector.increase.addEventListener('click', () => {
                const current = Number(selector.input.value) || min;

                selector.input.value = Math.min(
                    max,
                    current + 1
                );
                selector.input.dispatchEvent(new Event('input', { bubbles: true }));
                selector.input.dispatchEvent(new Event('change', { bubbles: true }));
            });

            selector.decrease.addEventListener('click', () => {
                const current = Number(selector.input.value) || min;

                selector.input.value = Math.max(
                    min,
                    current - 1
                );
                selector.input.dispatchEvent(new Event('input', { bubbles: true }));
                selector.input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }

        submitClick() {
            this.dom.submit.addEventListener('click', () => {
                if (this.isAdding) return;

                const selectedVariant = [
                    ...this.dom.radios
                ].find(radio => radio.checked);

                if (!selectedVariant) {
                    this.announce(window.languages.productFormQuickAddSelectSize);
                    return;
                }

                const variantId = Number(
                    selectedVariant.dataset.variantId
                );

                const quantity = Number(
                    this.dom.quantitySelector.input.value
                );

                if (
                    !Number.isInteger(variantId) ||
                    variantId <= 0 ||
                    !Number.isInteger(quantity) ||
                    quantity < 1 ||
                    quantity > 99
                ) {
                    this.announce(window.languages.productFormQuickAddInvalidQuantity);
                    return;
                }

                this.addToCart({
                    variantId,
                    quantity
                });
            });
        }

        async addToCart({ variantId, quantity }) {
            if (this.isAdding) return;

            this.isAdding = true;
            this.dom.submit.disabled = true;
            this.dom.submit.setAttribute('aria-busy', 'true');

            this.announce('');

            const root = window.Shopify?.routes?.root || '/';

            const formData = {
                items: [{
                    id: variantId,
                    quantity: quantity
                }]
            };

            try {
                const response = await fetch(
                    root + 'cart/add.js',
                    {
                        credentials: 'same-origin',
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest'
                        },
                        body: JSON.stringify(formData)
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    this.errorHandler({ errorData: data });
                    return;
                }

                // Keep existing Prestige cart drawer integration
                document.dispatchEvent(
                    new CustomEvent('product:added', {
                        bubbles: true,
                        detail: {
                            variant: variantId,
                            quantity: quantity
                        }
                    })
                );

                // Shopify add.js returns the added line item
                const addedItem = data.items?.[0] || data;
                const productTitle =
                    addedItem.product_title ||
                    addedItem.title ||
                    'Produktet';

                // Retrieve the updated cart quantity
                try {
                    const cartResponse = await fetch(
                        root + 'cart.js',
                        {
                            credentials: 'same-origin',
                            cache: 'no-store'
                        }
                    );

                    if (!cartResponse.ok) {
                        throw new Error(
                            'Could not retrieve updated cart'
                        );
                    }

                    const cart = await cartResponse.json();

                    const itemCount = Number(cart.item_count);

                    this.announce(
                        this.translatedMessage(
                            window.languages.productFormQuickAddAddedToCart,
                            { product: productTitle, count: itemCount }
                        )
                    );

                } catch (cartError) {
                    console.warn(
                        'Could not retrieve cart count:',
                        cartError
                    );

                    this.announce(
                        this.translatedMessage(
                            window.languages.productFormQuickAddAddedToCartNoCount,
                            { product: productTitle }
                        )
                    );
                }

            } catch (error) {
                console.error('Quick Add error:', error);

                this.announce(
                    window.languages.productFormQuickAddError
                );

            } finally {
                this.isAdding = false;
                this.dom.submit.disabled = false;
                this.dom.submit.removeAttribute('aria-busy');
            }
        }

        errorHandler({ errorData }) {
            const message =
                errorData?.description ||
                errorData?.message ||
                window.languages.productFormQuickAddError;

            this.announce(message);

        }
    }

    const initQuickAdd = () => {
        const quickAddElems = document.querySelectorAll(
            '.js-quick-add'
        );

        quickAddElems.forEach(quickAddElem => {
            new QuickAdd({
                elem: quickAddElem
            });
        });
    };

    initQuickAdd();

    document.addEventListener(
        'boostFilter:rendered',
        initQuickAdd
    );

    document.addEventListener(
        'Carousel:Build',
        initQuickAdd
    );
}
